import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "../Shared/Layout";
import toast from "react-hot-toast";
import { get, post, projectNo } from "../../_services/apiService";
import OrderSummary from "./OrderSummary";
import { API_URL } from "../../_services/apiUrl";
import { Toaster } from "react-hot-toast";
import AddressForm from "../ShippingAddress/AddressForm";
import UserAddress from "../ShippingAddress/UserAddress";
import { getLocationFromPincode } from "../../utils/googleapi";
import { getGuestId } from "../../utils/guestUtils";
import Breadcrumb from "../Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { getCountryOptions } from "../../utils/countries";

const RAZORPAY_SCRIPT_ID = "razorpay-checkout-js";
const PAYPAL_SCRIPT_ID = "paypal-checkout-js";

const loadRazorpayScript = () =>
    new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }

        const existingScript = document.getElementById(RAZORPAY_SCRIPT_ID);
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(true), { once: true });
            existingScript.addEventListener("error", () => resolve(false), { once: true });
            return;
        }

        const script = document.createElement("script");
        script.id = RAZORPAY_SCRIPT_ID;
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

const loadPayPalScript = ({ clientId, currency = "USD" }) =>
    new Promise((resolve) => {
        if (window.paypal) {
            resolve(true);
            return;
        }

        if (!clientId) {
            resolve(false);
            return;
        }

        const existingScript = document.getElementById(PAYPAL_SCRIPT_ID);
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(true), { once: true });
            existingScript.addEventListener("error", () => resolve(false), { once: true });
            return;
        }

        const script = document.createElement("script");
        const params = new URLSearchParams({
            "client-id": clientId,
            currency,
            intent: "capture",
        });

        script.id = PAYPAL_SCRIPT_ID;
        script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

const getDefaultForm = (customerInfo = null, guestIdInt = "") => ({
    id: null,
    contact_person_name:
        customerInfo?.name ||
        [customerInfo?.f_name, customerInfo?.l_name].filter(Boolean).join(" ") ||
        "",
    email: customerInfo?.email || "",
    address_type: "home",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: customerInfo?.phone || "",
    latitude: "09.090",
    longitude: "09.090",
    is_billing: 0,
    guest_id: String(guestIdInt || ""),
});

const getNormalizedPaymentMethod = (method = "") => {
    const value = String(method || "").trim().toLowerCase();

    if (value === "cash_on_delivery" || value === "cod" || value === "cash on delivery") {
        return "cash_on_delivery";
    }

    return "online_payment";
};

const getAddressComponent = (components = [], types = []) => {
    return components.find((component) =>
        types.some((type) => component?.types?.includes(type))
    )?.long_name || "";
};

const Checkout = () => {
    const location = useLocation();
    const initialPaymentMethod = useMemo(() => {
        const fromState = location?.state?.reorderPaymentMethod || sessionStorage.getItem("reorderPaymentMethod") || "online_payment";
        return getNormalizedPaymentMethod(fromState);
    }, [location]);
    const loginId = localStorage.getItem("loginId");
    const buyNowCartKey = localStorage.getItem("buyNowCartKey");

    const [loading, setLoading] = useState(false);
    const [discountApplied, setDiscountApplied] = useState(false);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [discountCode, setDiscountCode] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponMessage, setCouponMessage] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [guestIdInt, setGuestIdInt] = useState("");
    const [ipAddress, setIpAddress] = useState("");
    const [customerInfo, setCustomerInfo] = useState(null);
    const [userAddress, setUserAddress] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(
        localStorage.getItem("selectedAddressId") || ""
    );
    const [orderSummary, setOrderSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [formData, setFormData] = useState(getDefaultForm());
    const [paypalCheckout, setPaypalCheckout] = useState(null);

    const navigate = useNavigate();
    const paypalButtonContainerRef = useRef(null);
    const paypalButtonsRef = useRef(null);
    const countryOptions = useMemo(() => getCountryOptions(), []);

    useEffect(() => {
        const loadGuestId = async () => {
            try {
                const { int, raw } = await getGuestId();
                setGuestIdInt(int);
                setIpAddress(raw || "");
            } catch (error) {
                setGuestIdInt("");
                setIpAddress("");
            }
        };

        loadGuestId();
    }, []);

    const apiSummary = useMemo(() => orderSummary?.summary || {}, [orderSummary]);
    const apiCurrency = useMemo(() => orderSummary?.currency || {}, [orderSummary]);
    const checkoutCurrencySymbol = apiCurrency?.symbol || appliedCoupon?.currencySymbol || "";

    const getCheckoutCartId = useCallback(() => {
        if (buyNowCartKey) return String(buyNowCartKey || "");
        return "";
    }, [buyNowCartKey]);

    const fetchOrderSummary = useCallback(async (c_discount = 0) => {
        if (!selectedAddressId) {
            setOrderSummary(null);
            return;
        }

        try {
            setSummaryLoading(true);
            const cartId = getCheckoutCartId();
            const couponDiscountAmount = Number(c_discount || 0);
            const payload = {
                address_id: String(selectedAddressId),
                coupon_discount_amount:
                    couponDiscountAmount > 0 ? String(couponDiscountAmount) : 0,
                cart_id: cartId || "",
            };

            const response = await post(API_URL.orderSummary, payload);
            setOrderSummary(response?.data ?? null);
        } catch {
            setOrderSummary(null);
        } finally {
            setSummaryLoading(false);
        }
    }, [getCheckoutCartId, selectedAddressId]);

    useEffect(() => {
        if (!selectedAddressId) {
            setOrderSummary(null);
            return;
        }

        fetchOrderSummary(discountAmount);
    }, [selectedAddressId, discountAmount, fetchOrderSummary]);

    const cartTotals = useMemo(() => {
        const subtotalValue = Number(apiSummary?.subtotal || 0);
        const shippingValue = Number(apiSummary?.shipping_charge || 0);
        const discountValue = Number(apiSummary?.discount || 0);
        const couponValue = Number(apiSummary?.coupon_discount || 0);
        const fallbackTotal = subtotalValue + shippingValue - discountValue - couponValue;

        return {
            subtotal: subtotalValue,
            shipping: shippingValue,
            discount: discountValue,
            couponDiscount: couponValue,
            total: Number(apiSummary?.total ?? Math.max(0, fallbackTotal)),
        };
    }, [apiSummary]);

    const subtotal = Number(cartTotals?.subtotal || 0).toFixed(2);
    const cartTotal = Number(cartTotals?.subtotal || 0);
    const shippingAmount = Number(cartTotals?.shipping || 0);
    const productDiscountAmount = Number(cartTotals?.discount || 0);
    const summaryCouponDiscountAmount = Number(cartTotals?.couponDiscount || 0);
    const displayDiscountAmount = discountApplied
        ? summaryCouponDiscountAmount || Number(discountAmount || 0)
        : 0;
    const totalAmount = Number(cartTotals?.total || 0);

    const [productFormData, setProductFormData] = useState({
        addressId: localStorage.getItem("selectedAddressId")
            ? Number(localStorage.getItem("selectedAddressId"))
            : 0,
        orderId: 0,
        loginId: loginId ? Number(loginId) : 0,
        shippingCharge: 0,
        discount: 0,
        discountCode: "",
        referralCode: "",
        orderAmount: "0.00",
        paymentGatewayTypeId:
            initialPaymentMethod === "cash_on_delivery"
                ? 3
                : 1,
        paymentMethod: initialPaymentMethod,
        projectNo: projectNo,
        itemDetail: [],
        customOrderId: 0,
    });

    useEffect(() => {
        setProductFormData((prevData) => ({
            ...prevData,
            orderAmount: totalAmount.toFixed(2),
            addressId: selectedAddressId ? Number(selectedAddressId) : 0,
            shippingCharge: Number(shippingAmount || 0),
        }));
    }, [selectedAddressId, totalAmount, shippingAmount]);

    useEffect(() => {
        setProductFormData((prevData) => ({
            ...prevData,
            discount: discountApplied ? discountAmount : 0,
            discountCode: discountApplied ? discountCode : "",
        }));
    }, [discountApplied, discountAmount, discountCode]);

    useEffect(() => {
        if (productFormData.paymentMethod === "cash_on_delivery") return;

        const currencyCode = String(apiCurrency?.code || "").toUpperCase();
        const paymentGatewayTypeId = currencyCode === "USD" ? 2 : 1;

        setProductFormData((prevData) => ({
            ...prevData,
            paymentMethod: "online_payment",
            paymentGatewayTypeId,
        }));
    }, [apiCurrency?.code, productFormData.paymentMethod]);

    const resetForm = useCallback(() => {
        setFormData(getDefaultForm(customerInfo, guestIdInt));
    }, [customerInfo, guestIdInt]);

    const resetCheckoutState = useCallback(() => {
        setDiscountApplied(false);
        setDiscountAmount(0);
        setDiscountCode("");
        setAppliedCoupon(null);
        setCouponMessage("");
        setCouponCode("");
    }, []);

    const clearOrderCache = useCallback(() => {
        localStorage.removeItem("cartItemDetail");
        localStorage.removeItem("itemDetail");
        localStorage.removeItem("buyNowCartKey");
        sessionStorage.removeItem("reorderCartKeys");
        sessionStorage.removeItem("reorderPaymentMethod");
    }, []);

    const handleOrderSuccess = useCallback(async () => {
        clearOrderCache();
        resetCheckoutState();
    }, [clearOrderCache, resetCheckoutState]);

    const openRazorpayCheckout = useCallback(
        async (paymentData = {}) => {
            const isRazorpayLoaded = await loadRazorpayScript();

            if (!isRazorpayLoaded || !window.Razorpay) {
                toast.error("Unable to load Razorpay. Please try again.");
                return false;
            }

            if (!paymentData?.key || !paymentData?.order_id || !paymentData?.amount) {
                toast.error("Invalid Razorpay payment details received.");
                return false;
            }

            const orderId = paymentData.order_id;
            const notes =
                Array.isArray(paymentData?.notes) && paymentData.notes.length > 0
                    ? paymentData.notes[0]
                    : paymentData?.notes || {};

            const options = {
                key: paymentData.key,
                amount: Number(paymentData.amount),
                currency: paymentData.currency || "INR",
                name: paymentData.name || "MU Gems",
                description: paymentData.description || "Order Payment",
                image: paymentData.image || "",
                order_id: orderId,
                prefill: paymentData.prefill || {},
                notes,
                theme: {
                    color: "#fc722d",
                },
                handler: async () => {
                    await handleOrderSuccess();
                    window.location.href = `/paymentreturn/${orderId}`;
                },
                modal: {
                    ondismiss: () => {
                        toast.error("Payment cancelled.");
                    },
                },
            };

            const razorpayInstance = new window.Razorpay(options);

            razorpayInstance.on("payment.failed", (error) => {
                toast.error(
                    error?.error?.description ||
                    error?.error?.reason ||
                    "Payment failed. Please try again."
                );
            });

            razorpayInstance.open();
            return true;
        },
        [handleOrderSuccess]
    );

    const closePayPalCheckout = useCallback(() => {
        if (paypalButtonsRef.current?.close) {
            paypalButtonsRef.current.close();
        }

        paypalButtonsRef.current = null;
        setPaypalCheckout(null);
    }, []);

    const openPayPalCheckout = useCallback((paymentData = {}) => {
        const paypalOrderId =
            paymentData?.paypal_order_id ||
            paymentData?.orderID ||
            paymentData?.order_id ||
            "";
        const clientId =
            paymentData?.client_id ||
            paymentData?.paypal_client_id ||
            process.env.REACT_APP_PAYPAL_CLIENT_ID ||
            "";

        if (!paypalOrderId) {
            toast.error("Invalid PayPal payment details received.");
            return false;
        }

        if (!clientId) {
            toast.error("PayPal client id is missing.");
            return false;
        }

        setPaypalCheckout({
            clientId,
            currency: paymentData?.currency || "USD",
            paypalOrderId,
            orderId: paymentData?.order_id || "",
        });

        return true;
    }, []);

    useEffect(() => {
        if (!paypalCheckout || !paypalButtonContainerRef.current) return;

        let cancelled = false;

        const renderPayPalButtons = async () => {
            paypalButtonContainerRef.current.innerHTML = "";

            const isPayPalLoaded = await loadPayPalScript({
                clientId: paypalCheckout.clientId,
                currency: paypalCheckout.currency,
            });

            if (cancelled) return;

            if (!isPayPalLoaded || !window.paypal?.Buttons) {
                toast.error("Unable to load PayPal. Please try again.");
                closePayPalCheckout();
                return;
            }

            const buttons = window.paypal.Buttons({
                createOrder: () => paypalCheckout.paypalOrderId,
                onApprove: async (data) => {
                    toast.success("PayPal payment approved.");
                    await handleOrderSuccess();
                    closePayPalCheckout();
                    navigate(`/paymentreturn/${data?.orderID || paypalCheckout.paypalOrderId}`, {
                        state: {
                            orderId: paypalCheckout.orderId,
                            paypalOrderId: data?.orderID || paypalCheckout.paypalOrderId,
                            type: "paypal",
                        },
                    });
                },
                onCancel: () => {
                    toast.error("PayPal payment cancelled.");
                    closePayPalCheckout();
                },
                onError: () => {
                    toast.error("PayPal payment failed. Please try again.");
                    closePayPalCheckout();
                },
            });

            paypalButtonsRef.current = buttons;
            await buttons.render(paypalButtonContainerRef.current);
        };

        renderPayPalButtons();

        return () => {
            cancelled = true;
        };
    }, [
        closePayPalCheckout,
        handleOrderSuccess,
        navigate,
        paypalCheckout,
    ]);

    const handlePayPalModalClose = useCallback(() => {
        closePayPalCheckout();
        toast.error("PayPal payment cancelled.");
    }, [closePayPalCheckout]);

    const fetchCustomerInfo = useCallback(async () => {
        if (!guestIdInt) return;

        try {
            const response = await get(API_URL.customerInfo, { guest_id: guestIdInt });
            setCustomerInfo(response || null);

            setFormData((prev) => ({
                ...getDefaultForm(response, guestIdInt),
                ...prev,
                contact_person_name:
                    prev?.contact_person_name ||
                    response?.name ||
                    [response?.f_name, response?.l_name].filter(Boolean).join(" ") ||
                    "",
                phone: prev?.phone || response?.phone || "",
                email: prev?.email || response?.email || "",
                guest_id: String(guestIdInt),
            }));
        } catch (error) {
            setCustomerInfo(null);
        }
    }, [guestIdInt]);

    const fetchUserAddress = useCallback(async () => {
        if (!guestIdInt) return;

        try {
            setLoading(true);
            const response = await get(API_URL.getAddress, { guest_id: guestIdInt });
            const finalData = response?.data || response || [];

            if (Array.isArray(finalData) && finalData.length > 0) {
                setUserAddress(finalData);

                const savedId = localStorage.getItem("selectedAddressId");
                const matched = savedId
                    ? finalData.find((item) => String(item?.id) === String(savedId))
                    : null;
                const nextSelectedId = String(matched?.id || finalData[0]?.id || "");

                setSelectedAddressId(nextSelectedId);
                if (nextSelectedId) {
                    localStorage.setItem("selectedAddressId", nextSelectedId);
                }

                setShowForm(false);
            } else {
                setUserAddress([]);
                setSelectedAddressId("");
                localStorage.removeItem("selectedAddressId");
                setShowForm(true);
                setFormData(getDefaultForm(customerInfo, guestIdInt));
            }
        } catch (error) {

            setUserAddress([]);
            setSelectedAddressId("");
            localStorage.removeItem("selectedAddressId");
            setShowForm(true);
            setFormData(getDefaultForm(customerInfo, guestIdInt));
        } finally {
            setLoading(false);
        }
    }, [customerInfo, guestIdInt]);

    useEffect(() => {
        if (!guestIdInt) return;
        fetchCustomerInfo();
    }, [fetchCustomerInfo, guestIdInt]);

    useEffect(() => {
        if (!guestIdInt) return;
        fetchUserAddress();
    }, [fetchUserAddress, guestIdInt]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;

        if (name === "phone") {
            finalValue = value.replace(/\D/g, "").slice(0, 15);
        }

        if (name === "zip") {
            finalValue = value.replace(/[^a-zA-Z0-9\s-]/g, "").slice(0, 12);

            if (finalValue.length >= 3) {
                getAddressByZip(finalValue);
                return;
            }
        }

        setFormData((prev) => ({ ...prev, [name]: finalValue }));
    };

    const handleAddressSelect = (addressId) => {
        const finalId = String(addressId);
        setSelectedAddressId(finalId);
        localStorage.setItem("selectedAddressId", finalId);
        setProductFormData((prev) => ({
            ...prev,
            addressId: Number(finalId),
        }));
    };

    const handleAddAddressClick = () => {
        resetForm();
        setShowForm(true);
    };

    const handleEditAddress = (item) => {
        setFormData({
            id: item?.id || null,
            contact_person_name: item?.contact_person_name || customerInfo?.name || "",
            email: item?.email || customerInfo?.email || "",
            address_type: item?.address_type || "home",
            address: item?.address || "",
            city: item?.city || "",
            state: item?.state || "",
            zip: item?.zip || "",
            country: item?.country || "",
            phone: item?.phone || customerInfo?.phone || "",
            latitude: item?.latitude || "09.090",
            longitude: item?.longitude || "09.090",
            is_billing:
                item?.is_billing === true || item?.is_billing === 1 || item?.is_billing === "1"
                    ? 1
                    : 0,
            guest_id: String(guestIdInt || ""),
        });
        setShowForm(true);
    };

    const handleBackToAddresses = () => {
        setShowForm(false);
        resetForm();
    };

    const validateForm = () => {
        if (!formData.contact_person_name?.trim()) {
            toast.error("Name is required");
            return false;
        }
        if (!formData.phone?.trim()) {
            toast.error("Phone is required");
            return false;
        }
        if (!formData.country?.trim()) {
            toast.error("Country is required");
            return false;
        }
        if (!formData.state?.trim()) {
            toast.error("State is required");
            return false;
        }
        if (!formData.city?.trim()) {
            toast.error("City is required");
            return false;
        }
        if (!formData.zip?.trim()) {
            toast.error("Zip is required");
            return false;
        }
        if (!formData.address?.trim()) {
            toast.error("Address is required");
            return false;
        }
        return true;
    };

    const onUserDetailSave = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setLoading(true);

            const payload = {
                contact_person_name: formData.contact_person_name.trim(),
                email: formData.email || "",
                address_type: "home",
                address: formData.address.trim(),
                city: formData.city.trim(),
                zip: formData.zip.trim(),
                country: formData.country.trim(),
                state: formData.state.trim(),
                phone: formData.phone.trim(),
                latitude: formData.latitude || "09.090",
                longitude: formData.longitude || "09.090",
                is_billing: Number(formData.is_billing || 0),
                guest_id: String(guestIdInt || formData.guest_id || ""),
            };

            if (formData?.id) {
                payload.id = formData.id;
            }

            const response = formData?.id
                ? await post(API_URL.updateAddress, payload)
                : await post(API_URL.addAddress, payload);

            if (response?.status === 1 || response?.isSuccess === 1 || response?.message) {
                toast.success(
                    response?.message ||
                    (formData?.id ? "Address updated successfully" : "Address added successfully")
                );
                resetForm();
                await fetchUserAddress();
            } else {
                toast.error(
                    response?.message ||
                    (formData?.id ? "Failed to update address" : "Failed to add address")
                );
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data?.errors?.[0]?.message ||
                "Something went wrong"
            );
        } finally {
            setLoading(false);
        }
    };

    const getAddressByZip = async (pin) => {
        setFormData((prev) => ({ ...prev, zip: pin }));

        try {
            const data = await getLocationFromPincode(pin);

            if (data?.status === "OK" && data?.results?.length > 0) {
                const components = data.results[0].address_components;

                const city =
                    getAddressComponent(components, [
                        "locality",
                        "postal_town",
                        "administrative_area_level_2",
                        "sublocality",
                        "sublocality_level_1",
                    ]);
                const state = getAddressComponent(components, [
                    "administrative_area_level_1",
                ]);
                const country = getAddressComponent(components, ["country"]);
                const postalCode = getAddressComponent(components, ["postal_code"]) || pin;

                setFormData((prev) => ({
                    ...prev,
                    zip: postalCode,
                    city,
                    state,
                    country: country || prev.country,
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    zip: pin,
                    city: "",
                    state: "",
                    country: prev.country || "",
                }));
            }
        } catch (err) {
        }
    };

    const getCouponCode = async () => {
        if (!couponCode) {
            toast.error("Please enter coupon code.");
            return;
        }

        setLoading(true);

        try {
            const response = await get(API_URL?.CouponList);
            const coupons = response?.coupons || [];

            const matchedCoupon = coupons.find(
                (item) => item.code.toLowerCase() === couponCode.toLowerCase()
            );

            if (!matchedCoupon) {
                toast.error("Invalid coupon code");
                setDiscountApplied(false);
                return;
            }

            if (cartTotal < matchedCoupon.min_purchase) {
                toast.error(`Minimum purchase should be ${checkoutCurrencySymbol} ${matchedCoupon.min_purchase}`);
                setDiscountApplied(false);
                return;
            }

            let discountValue = 0;

            if (matchedCoupon.discount_type === "percentage") {
                discountValue = (cartTotal * matchedCoupon.discount) / 100;
            } else {
                discountValue = matchedCoupon.discount;
            }

            toast.success("Coupon applied successfully");

            setDiscountAmount(discountValue);
            setDiscountCode(matchedCoupon.code);
            setDiscountApplied(true);

            setProductFormData((prevData) => ({
                ...prevData,
                discount: discountValue,
                discountCode: matchedCoupon.code,
            }));
        } catch (error) {
            toast.error("Something went wrong");
            setDiscountApplied(false);
        } finally {
            setLoading(false);
            setCouponCode("");
        }
    };

    const applyCoupon = async (couponCodeStatic = "") => {
        try {
            const typedCouponCode =
                typeof couponCodeStatic === "string" ? couponCodeStatic : "";
            const finalCouponCode = typedCouponCode.trim() || couponCode?.trim();

            if (!finalCouponCode) {
                setCouponMessage("Please enter a coupon code.");
                toast.warning("Please enter a coupon code.");
                return;
            }

            setCouponLoading(true);

            const response = await post(API_URL.applyCoupon, {
                code: finalCouponCode,
                ip_address: ipAddress,
                address_id: selectedAddressId,
            });

            const couponData = response?.data || response;

            if (
                couponData &&
                couponData.coupon_discount !== undefined &&
                couponData.coupon_discount !== null
            ) {
                const discount = Number(couponData.coupon_discount || 0);
                const couponCurrencySymbol =
                    couponData?.currency_symbol || checkoutCurrencySymbol;

                setCouponCode(finalCouponCode);
                setDiscountCode(finalCouponCode);
                setDiscountAmount(discount);
                setDiscountApplied(true);
                setAppliedCoupon({
                    code: finalCouponCode,
                    discount,
                    type: couponData?.coupon_type || "",
                    currencyCode: couponData?.currency_code || "",
                    currencySymbol: couponData?.currency_symbol || "",
                });

                setCouponMessage(
                    `Coupon applied! You saved ${couponCurrencySymbol} ${discount.toFixed(2)}`
                );
                await fetchOrderSummary(discount);
                toast.success("Coupon code applied successfully");
            } else {
                setDiscountAmount(0);
                setDiscountCode("");
                setDiscountApplied(false);
                setAppliedCoupon(null);
                setCouponMessage("Invalid coupon code");
                await fetchOrderSummary(0);
                toast.error("Invalid coupon code");
            }
        } catch (error) {
            setDiscountAmount(0);
            setDiscountCode("");
            setDiscountApplied(false);
            setAppliedCoupon(null);
            setCouponMessage("Failed to apply coupon");
            await fetchOrderSummary(0);
            toast.error(error?.response?.data?.message || "Failed to apply coupon");
        } finally {
            setCouponLoading(false);
        }
    };

    void getCouponCode;

    const handlePaymentMethodChange = (event) => {
        const selectedPaymentMethod = event.target.value;

        if (selectedPaymentMethod === "cash_on_delivery") {
            setDiscountAmount(0);
            setDiscountCode("");
            setDiscountApplied(false);
            setAppliedCoupon(null);
            setCouponMessage("");

            setProductFormData((prevData) => ({
                ...prevData,
                discount: 0,
                discountCode: "",
                paymentMethod: selectedPaymentMethod,
                paymentGatewayTypeId: 3,
            }));
        } else {
            const currencyCode = String(apiCurrency?.code || "").toUpperCase();
            setProductFormData((prevData) => ({
                ...prevData,
                paymentMethod: selectedPaymentMethod,
                paymentGatewayTypeId: currencyCode === "USD" ? 2 : 1,
            }));
        }
    };

    const onCreateOrder = async () => {
        const finalAddressId = selectedAddressId
            ? Number(selectedAddressId)
            : Number(productFormData?.addressId || 0);
        const summaryCartCount = Number(orderSummary?.cart_count || 0);

        if (!finalAddressId) {
            toast.error("Please select address first");
            return;
        }

        if (!summaryCartCount) {
            toast.error("Cart is empty");
            return;
        }

        try {
            setLoading(true);

            const selectedPaymentMethod = productFormData?.paymentMethod || "online_payment";
            const currencyCode = String(apiCurrency?.code || "").toUpperCase();
            const onlinePaymentMethod = currencyCode === "USD" ? "paypal" : "razorpay";
            const paymentMethod =
                selectedPaymentMethod === "cash_on_delivery"
                    ? "cash_on_delivery"
                    : onlinePaymentMethod;
            const paymentGatewayTypeId =
                paymentMethod === "cash_on_delivery" ? 3 : paymentMethod === "paypal" ? 2 : 1;
            const appliedCouponCode =
                discountApplied
                    ? appliedCoupon?.code || discountCode || couponCode?.trim() || ""
                    : "";
            const appliedCouponDiscount =
                discountApplied
                    ? Number(displayDiscountAmount || discountAmount || appliedCoupon?.discount || 0)
                    : 0;
            const cartId = getCheckoutCartId();
            const orderAmount =
                paymentMethod === "cash_on_delivery"
                    ? Math.max(
                        0,
                        Number(cartTotals?.subtotal || 0) +
                        Number(cartTotals?.shipping || 0) -
                        Number(cartTotals?.discount || 0) -
                        Number(appliedCouponDiscount || 0)
                    )
                    : Number(totalAmount || 0);

            const response = await post(API_URL.placeOrder, {
                address_id: finalAddressId,
                billing_address_id: finalAddressId,
                payment_method: paymentMethod,
                payment_gateway_type_id: paymentGatewayTypeId,
                coupon_code: appliedCouponCode,
                coupon_discount: appliedCouponDiscount,
                product_discount: Number(productDiscountAmount || 0),
                shipping_charge: Number(shippingAmount || 0),
                order_amount: Number(orderAmount || 0).toFixed(2),
                ip_address: ipAddress || "",
                cart_id: cartId || "",
            });

            const isOrderCreated =
                response?.status === true ||
                Number(response?.status) === 1 ||
                Number(response?.isSuccess) === 1 ||
                Number(response?.isSuccess) === 2 ||
                Number(response?.isSuccess) === 3 ||
                Number(response?.isSuccess) === 200;

            if (!isOrderCreated) {
                toast.error(response?.message || "Failed to create order");
                return;
            }

            if (paymentMethod === "cash_on_delivery") {
                toast.success("Order placed successfully (COD)");
                await handleOrderSuccess();
                navigate("/paymentreturn/cash_on_delivery", {
                    state: {
                        orderId: response?.order_id || response?.data?.order_id,
                        type: "cod",
                    },
                });
                return;
            }

            if (paymentMethod === "paypal") {
                const paymentOpened = openPayPalCheckout(response?.data || response || {});

                if (!paymentOpened) {
                    toast.error("Unable to start PayPal payment.");
                }

                return;
            }

            const paymentOpened = await openRazorpayCheckout(response?.data || {});
            if (!paymentOpened) {
                toast.error("Unable to start Razorpay payment.");
            }

            // const isOrderCreated =
            //     response?.status === true ||
            //     Number(response?.status) === 1 ||
            //     Number(response?.isSuccess) === 1 ||
            //     Number(response?.isSuccess) === 2 ||
            //     Number(response?.isSuccess) === 3 ||
            //     Number(response?.isSuccess) === 200;

            // if (!isOrderCreated) {
            //     toast.error(response?.message || "Failed to create order");
            //     return;
            // }

            // // ✅ CASE 1: Razorpay
            // if (Number(response?.isSuccess) === 1) {
            //     const paymentOpened = await openRazorpayCheckout(response?.data || {});
            //     if (!paymentOpened) {
            //         toast.error("Unable to start Razorpay payment.");
            //     }
            //     return;
            // }

            // // ✅ CASE 2: Cash on Delivery
            // if (Number(response?.isSuccess) === 2) {
            //     toast.success("Order placed successfully (COD)");
            //     await handleOrderSuccess();

            //     // 👉 IMPORTANT: redirect to success page
            //     navigate("/paymentreturn/cash_on_delivery", {
            //         state: {
            //             orderId: response?.order_id || response?.data?.order_id,
            //             type: "cod",
            //         },
            //     });
            //     return;
            // }

            // if (Number(response?.isSuccess) === 3) {
            //     const paymentOpened = openPayPalCheckout(response?.data || response || {});

            //     if (!paymentOpened) {
            //         toast.error("Unable to start PayPal payment.");
            //     }

            //     return;
            // }

            // const redirectUrl =
            //     response?.redirect_url ||
            //     response?.url ||
            //     response?.data?.url ||
            //     response?.data?.redirect_url;

            // if (redirectUrl) {
            //     window.location.href = redirectUrl;
            // } else if (response?.message || response?.order_id || response?.data?.order_ids) {
            //     toast.success(response?.message || "Order placed successfully");
            //     await handleOrderSuccess();
            //     navigate("/paymentreturn/cash_on_delivery");
            // } else {
            //     toast.error(response?.message || "Failed to create order");
            // }
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Payment Failed. Please try again later."
            );
        } finally {
            setLoading(false);
        }
    };

    const removeCoupon = async () => {
        setDiscountAmount(0);
        setDiscountCode("");
        setDiscountApplied(false);
        setAppliedCoupon(null);
        setCouponMessage("");
        setCouponCode("");
        toast.success("Removed Coupon Code");

        setProductFormData((prevData) => ({
            ...prevData,
            discount: 0,
            discountCode: "",
        }));

        await fetchOrderSummary(0);
    };

    const showAddressList = userAddress.length > 0 && !showForm;

    return (
        <Layout showBreadcrumb={false}>
            <Toaster position="top-right" />
            {paypalCheckout && (
                <div
                    className="modal d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ backgroundColor: "rgba(0, 0, 0, 0.65)" }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Pay with PayPal</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={handlePayPalModalClose}
                                />
                            </div>
                            <div className="modal-body">
                                <div ref={paypalButtonContainerRef} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <section className="same_space">
                <div className="container">
                    <section className="contact-banner-sec">
                        <div className="container-fluid">
                            <div className="page-header">
                                <h1>Checkout</h1>
                            </div>
                            <Breadcrumb />
                        </div>
                    </section>

                    <div className="all_page_heading">
                        <h5 className="text-white">Delivery Address</h5>
                    </div>

                    <div className="row mt-lg-4 mt-0">
                        <div className="col-xl-8 col-md-7 col-12 pb-4 mb-4">
                            {loading && userAddress.length === 0 && !showForm ? (
                                <div className="box-border">
                                    <span className="text-white">Loading addresses...</span>
                                </div>
                            ) : showAddressList ? (
                                <UserAddress
                                    userAddress={userAddress}
                                    handleAddressSelect={handleAddressSelect}
                                    handleAddAddressClick={handleAddAddressClick}
                                    onEdit={handleEditAddress}
                                    selectedAddressId={selectedAddressId}
                                />
                            ) : (
                                <AddressForm
                                    onUserDetailSave={onUserDetailSave}
                                    handleChange={handleChange}
                                    formData={formData}
                                    loading={loading}
                                    handleBackToAddresses={
                                        userAddress.length > 0 ? handleBackToAddresses : null
                                    }
                                    isEditMode={!!formData?.id}
                                    countryOptions={countryOptions}
                                />
                            )}
                        </div>

                        <div className="d-flex justify-content-center col-xl-4 col-md-5 col-12 mb-4">
                            <OrderSummary
                                subtotal={subtotal}
                                shippingAmount={shippingAmount}
                                productDiscountAmount={productDiscountAmount}
                                discountAmount={displayDiscountAmount}
                                totalAmount={totalAmount}
                                discountApplied={discountApplied}
                                couponLoading={couponLoading}
                                couponMessage={couponMessage}
                                appliedCoupon={appliedCoupon}
                                handlePaymentMethodChange={handlePaymentMethodChange}
                                productFormData={productFormData}
                                loading={loading || summaryLoading}
                                onCreateOrder={onCreateOrder}
                                removeCoupon={removeCoupon}
                                setCouponCode={setCouponCode}
                                couponCode={couponCode}
                                applyCoupon={applyCoupon}
                                currencySymbol={checkoutCurrencySymbol}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Checkout;
